import { FormControlLabel, Switch, Typography, Box } from '@material-ui/core';
import { DictionariesService, DictionaryItem } from '@sa/rep-mdm';
import * as React from 'react';
import { combineLatest, Subscription } from 'rxjs';
import styled from 'styled-components';
import { useHookSubscribe } from '../../../../react-rxjs';
import { useMapStore } from '../../../hooks/useMapStore';
import { useService } from '../../../hooks/useService';
import { ServicesMaps } from '../../../MapReportingProvider';
import { MapDataSourceId } from '../../../models/MapDataSource';
import { MapUnit } from '../../../models/MapUnit';
import { MapAttribute } from '../../../models/MapAttribute';
import { useLeafletTooltip } from '../../LeafletMap/Leaflet.hooks';

const StyledBox = styled(Box)(props => ({

}));
const StyledText = styled(Typography)(props => ({
  fontSize: '12px'
}));

export const Tooltip = () => {
  const store = useMapStore();
  const dictionaries = useService<DictionariesService>(ServicesMaps.Dictionaries);
  const dataSourceId = useHookSubscribe(MapDataSourceId.Default, store.dataSourceId$);
  const dimensions = useHookSubscribe([], store.dimensions$);
  const [reportingCountry, setReportingCountry] = React.useState<undefined | DictionaryItem>();
  const [commodity, setCommodity] = React.useState<undefined | string>();
  const [flow, setFlow] = React.useState<undefined | string>();
  React.useEffect(() => {
    const subscribtions: (Subscription | undefined)[] = [];
    if (dataSourceId === MapDataSourceId.UNComtrade && dimensions.every(e => e.dataSource?.id === dataSourceId)) {
      const reportingCountryDim = dimensions.find(e => e.hierarchy.includes('reporting_countries.'));
      if (reportingCountryDim) {
        const value$ = store.dimensionsValues.get(reportingCountryDim.id);
        const subscribe = value$?.subscribe(async countryId => {
          const countryDictionary = await dictionaries.getDictionary({ id: reportingCountryDim.identifier, parameters: {} });
          const countries = await countryDictionary.getAll();
          const country = countries.find(e => e.id === String(countryId));
          setReportingCountry(country);
        });
        subscribtions.push(subscribe);
      } else {
        setReportingCountry(undefined);
      }

      ([['commodities.', setCommodity], ['flow.', setFlow]] as [string, React.Dispatch<React.SetStateAction<string | undefined>>][])
        .forEach(([hierarchy, setState]) => {
          const dimension = dimensions.find(e => e.hierarchy.includes(hierarchy));
          if (dimension) {
            const value$ = store.dimensionsValues.get(dimension.id);
            const subscribe = value$?.subscribe(async value => {
              const dictionary = await dictionaries.getDictionary({ id: dimension.identifier, parameters: {} });
              const dictItems = await dictionary.getAll();
              const itemName = dictItems.find(e => e.id === String(value))?.getValue<Record<number, string>>('name')?.[9]!;
              setState(itemName);
            });
            subscribtions.push(subscribe);
          } else {
            setState(undefined);
          }
        })
    } else {
      [setReportingCountry, setCommodity, setFlow].forEach(f => f(undefined));
    }

    return () => subscribtions.forEach(e => e?.unsubscribe());
  }, [dataSourceId, dimensions, dictionaries]);

  const layer = useLeafletTooltip();
  const feature = layer?.['feature'] as any;
  const properties = feature?.properties;
  const iso2cd = properties?.ISO2CD;
  const value = properties?.value;
  const displayValue = properties?.displayValue;
  const sourceName = properties?.source;
  const unitName = properties?.unit;
  const unionEU28 = properties?.UnionEU28;
  const hasData = properties?.hasData && value !== undefined && value !== null;
  const notInSource = properties?.notInSource;
  const reportCountryCD = reportingCountry?.getValue<string>('code');
  const message = React.useMemo(() => {
    if (notInSource || !hasData) {
      return notInSource ? 'Not in source' : 'No data';
    }
    
    if (dataSourceId === MapDataSourceId.UNComtrade) {
      if (reportCountryCD === iso2cd) {
        return 'Reporting country';
      }
      return `${displayValue} (${unitName})`;
    }
    
    if (dataSourceId === MapDataSourceId.HarmonizedDataset) {
      return `${displayValue} (${unitName})`;
    }
    
    return `${displayValue} ${unitName}`;
  }, [displayValue, hasData, notInSource, reportCountryCD, unitName, iso2cd]);
  
  const name = properties?.name;
  const ROMNAM = properties?.ROMNAM;
  const countryName = (unionEU28 || !name) ? ROMNAM : name;
  if (dataSourceId === MapDataSourceId.UNComtrade && reportCountryCD !== iso2cd) {
    const reportingCountryName = reportingCountry?.getValue<string>('name') || '';
    return <StyledBox>
      <StyledText variant="body1">{flow} from {reportingCountryName} to {countryName}</StyledText>
      <StyledText variant="body2">{commodity}</StyledText>
      <StyledText variant="body2">{message}</StyledText>
    </StyledBox>
  }
  if (dataSourceId === MapDataSourceId.HarmonizedDataset) {
    return <StyledBox>
      <StyledText variant="body1">{countryName}</StyledText>
      <StyledText variant="body2">{message}</StyledText>
      <StyledText variant="body2">{sourceName}</StyledText>
    </StyledBox>
  }
  return <StyledBox>
    <StyledText variant="body1">{countryName}</StyledText>
    <StyledText variant="body2">{message}</StyledText>
  </StyledBox>
}
